import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  DonationPrompt,
  DonationPromptsFragment,
} from '../../../types/__generated__';
import { DonationPromptsSkeleton } from './DonationPromptsSkeleton';

import { useOptimizely, usePageMeta } from '../../hooks';
import { useTranslation } from 'react-i18next';
import PageSection, { PageSectionProps } from '../PageSection/PageSection';
import DonateButton from '../DonateButton/DonateButton';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import parseCurrency from '../../utils/parseCurrency';
import { Experiments } from '../../constants/experiments';
import isDesktopViewport from '../../utils/isDesktopViewport';
import usePageQuery, { buildQuery } from '../../hooks/usePageQuery';

const FRAGMENT = buildQuery(gql`
  fragment DonationPrompts on Page {
    id
    relationships {
      beneficiaries(first: 1) {
        totalCount
        nodes {
          ... on Charity {
            id
            legacyId
            name
            countryCode
            displayCurrency
            externalId
            profilePageUrl
            donationPrompts {
              description
              amount
              type
            }
          }
        }
      }
    }
  }
`);

const isOneOff = (donationPrompt: DonationPrompt) =>
  donationPrompt.type === 'OneOff';

const DonationPrompts: FunctionComponent = () => {
  const { pageType } = usePageMeta();
  const { t } = useTranslation('common', { useSuspense: false });
  const optimizely = useOptimizely();

  const isOnePage = pageType === 'one_page';
  const isDesktop = typeof window !== 'undefined' ? isDesktopViewport() : false;

  const { loading, error, data, refetch } = usePageQuery<
    DonationPromptsFragment
  >(FRAGMENT);

  const pageSectionProps: PageSectionProps = {
    hasBorder: isOnePage ? (isDesktop ? false : true) : true,
    className: '',
    hasBottomBorder: isDesktop ? true : false,
  };

  if (loading) {
    return (
      <PageSection {...{ ...pageSectionProps, titleId: 'donation-prompts1' }}>
        <DonationPromptsSkeleton animated displayRefreshButton={false} />
      </PageSection>
    );
  }

  if (error) {
    return (
      <PageSection {...{ ...pageSectionProps, titleId: 'donation-prompts2' }}>
        <DonationPromptsSkeleton onRefresh={refetch} />
      </PageSection>
    );
  }

  const beneficiary = data?.relationships?.beneficiaries?.nodes[0];

  const oneOffdonationPrompts =
    beneficiary?.__typename === 'Charity'
      ? beneficiary?.donationPrompts?.filter(isOneOff)
      : null;
  const currencyCode =
    (beneficiary?.__typename === 'Charity' && beneficiary?.displayCurrency) ||
    'GBP';

  if (!oneOffdonationPrompts?.length) {
    return null;
  }

  const donationPromptsDecision = optimizely?.decide(
    Experiments.ConsumerDarkslide.DonationPrompts.flagKey,
  );

  return donationPromptsDecision?.enabled ? (
    <PageSection
      {...{
        ...pageSectionProps,
        titleId: 'donation-prompts',
        title: t('donationPrompts.title', 'Donation Impact'),
      }}
    >
      {oneOffdonationPrompts.map(
        ({ amount, description }: DonationPrompt, index) => (
          <DonationPromptItem
            key={`donationPrompt-${index}`}
            amount={amount!}
            description={description!}
            displayCurrency={currencyCode}
          ></DonationPromptItem>
        ),
      )}

      <DonateButton
        className="jg-space-mtsm"
        id="pages-ui-donation-prompts-donate-button"
      />
    </PageSection>
  ) : null;
};

interface DonationPromptsItemProps {
  amount: number;
  description: string;
  displayCurrency: string;
}

const DonationPromptItem = ({
  amount,
  description,
  displayCurrency,
}: DonationPromptsItemProps) => {
  return (
    <div
      className={classNames(
        'jg-display-flex jg-flex-aic jg-space-psm jg-space-mvsm jg-background--white',
      )}
    >
      <div className={classNames('jg-text--bold jg-space-mrsm')}>
        <strong>
          {parseCurrency(displayCurrency, amount, {
            roundDown: true,
            wholeNumber: true,
          })}
        </strong>
      </div>
      <div>{description}</div>
    </div>
  );
};

export default DonationPrompts;
